/**
 * KPI stat tiles (numeric + text) for the delivery console KPI band.
 *
 * Anatomy per DESIGN-SPEC.md §2.11/§1.5 (reference "cargo layout" card
 * language): borderless surface-1 card, a small icon chip + muted label on
 * one line, a large bold value beneath, a context caption last. Replaces the
 * old bordered/top-accent-strip card — the "old app, new colors" complaint.
 */
import type { ReactNode } from 'react';
import { sectionCardStyle } from './chartTokens';

// ---------------------------------------------------------------------------
// Icon glyphs — 16px stroke, 1.5px, round caps (§1.6). Shared by every KPI
// tile call site (StatKpiBand, composed by StatKpisHost inside the M2
// dashboard canvas — M2 T5) so the same five destinations always carry the
// same glyph.
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

export function IconStatProjects() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

export function IconStatActive() {
  return (
    <svg {...ICON_PROPS}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  );
}

export function IconStatDone() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

export function IconStatCalendar() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="5" width="18" height="16" rx="2.2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="2.5" x2="8" y2="6.5" />
      <line x1="16" y1="2.5" x2="16" y2="6.5" />
    </svg>
  );
}

export function IconStatAlert() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tone — drives the value color only (chrome stays monochrome per §1.2).
// ---------------------------------------------------------------------------

export type StatTileTone = 'default' | 'positive' | 'warning' | 'critical';

const TONE_COLOR: Record<StatTileTone, string> = {
  default:  'var(--text-hi)',
  positive: 'var(--sem-green)',
  warning:  'var(--sem-amber)',
  critical: 'var(--sem-red)',
};

// ---------------------------------------------------------------------------
// Shared chrome — icon chip + label header row, used by both tile variants.
// ---------------------------------------------------------------------------

function TileHeader({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        aria-hidden="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--surface-2)',
          color: 'var(--text-low)',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--text-low)',
          letterSpacing: '0.01em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tile — numeric value
// ---------------------------------------------------------------------------
interface StatTileProps {
  label: string;
  value: number;
  icon: ReactNode;
  tone?: StatTileTone;
  sub: string;
}

export function StatTile({ label, value, icon, tone = 'default', sub }: StatTileProps) {
  return (
    // minWidth:0 + overflow:hidden — CSS grid's automatic minimum size floors
    // a 1fr track at each tile's min-content (icon chip + padding), so a
    // narrowed StatKpiBand bleeds horizontally UNCLIPPED unless every tile
    // opts out and clips (clip floors audit finding 12).
    <div style={sectionCardStyle({ padding: 16, height: '100%', minWidth: 0, overflow: 'hidden' })} className="hover-lift">
      <TileHeader icon={icon} label={label} />
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 28,
          fontWeight: 700,
          color: TONE_COLOR[tone],
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
          lineHeight: 1,
          margin: '16px 0 6px',
        }}
      >
        {value}
      </p>
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--text-low)',
          margin: 0,
        }}
      >
        {sub}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tile — string value (date / text)
// ---------------------------------------------------------------------------
interface StatTileTextProps {
  label: string;
  value: string | null;
  icon: ReactNode;
  tone?: StatTileTone;
  sub: string;
  emptyValue?: string;
  testId?: string;
}

export function StatTileText({
  label,
  value,
  icon,
  tone = 'default',
  sub,
  emptyValue = '—',
  testId,
}: StatTileTextProps) {
  return (
    // Same grid automatic-minimum opt-out + clip as StatTile above.
    <div data-testid={testId} style={sectionCardStyle({ padding: 16, height: '100%', minWidth: 0, overflow: 'hidden' })} className="hover-lift">
      <TileHeader icon={icon} label={label} />
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: value ? 20 : 28,
          fontWeight: 700,
          color: value ? TONE_COLOR[tone] : 'var(--text-low)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
          lineHeight: 1,
          margin: '16px 0 6px',
        }}
      >
        {value ? value.slice(0, 10) : emptyValue}
      </p>
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--text-low)',
          margin: 0,
        }}
      >
        {sub}
      </p>
    </div>
  );
}
